import React, {DragEvent, useCallback, useEffect, useRef, useState} from 'react';
import {album} from "../../../models/album";
import {photo} from "../../../models/photo";
import {albumResultType} from "../../../types/Api/AlbumResultType";
import {photoResultCollectionType, photoResultType} from "../../../types/Api/PhotoResultType";
import {photoHydrator} from "../../../models/hydrator/photoHydrator";
import {albumHydrator} from "../../../models/hydrator/albumHydrator";
import {ApiQueryParam, ApiService} from "../../../service/ApiService";
import {UploadService} from "../../../service/UploadService";
import {DeleteConfirme} from "../DeleteConfirme";
import {Lightbox} from "../../front/album/Lightbox";
import {TableLineLoader} from "../../front/loader/TableLineLoader";

type PhotoManagerProps = {
  album: album,
  change: (album: album) => void,
}

type uploadState = {
  done: number;
  total: number;
  failed: string[];
}

export const PhotoManager = ({album, change}: PhotoManagerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([] as photo[]);
  const [upload, setUpload] = useState(undefined as undefined | uploadState);
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState([] as string[]);
  const [del, setDel] = useState(undefined as undefined | photo[]);
  const [preview, setPreview] = useState(undefined as undefined | number);
  const [busy, setBusy] = useState(false);

  const findPhoto = useCallback(async (): Promise<void> => {
    const apiFilters: ApiQueryParam = new ApiQueryParam();

    apiFilters.addFilter({
      field: 'albumId',
      value: album.albumId
    });
    apiFilters.addSort({
      by: 'createdAt',
      direction: 'ASC'
    });
    apiFilters.setCount(0);

    setLoading(true);

    try {
      const result = await ApiService.get('/photo', apiFilters) as photoResultCollectionType;

      setPhotos(result._embedded.photos.map((p: photoResultType) => photoHydrator.hydrate(p)));
    } catch (_) {
      alert("Une erreur c'est produite.");
    } finally {
      setLoading(false);
    }
  }, [album.albumId]);

  useEffect(() => {
    findPhoto();
  }, [findPhoto]);

  const updateAlbum = async (data: { src: string }): Promise<void> => {
    const result = await ApiService.patch(`/album/${album.albumId}`, {param: () => data}) as albumResultType;

    change(albumHydrator.hydrate(result));
  }

  const uploadFiles = async (files: File[]): Promise<void> => {
    const images = files.filter((f: File) => f.type.startsWith('image/'));

    if (images.length === 0 || upload !== undefined) {
      return;
    }

    const state: uploadState = {done: 0, total: images.length, failed: []};
    setUpload({...state});

    for (const file of images) {
      try {
        const src = await UploadService.resizeImageToBase64(file);
        await ApiService.post('/photo', {param: () => ({albumId: album.albumId, src: src})});
      } catch (_) {
        state.failed.push(file.name);
      }

      state.done++;
      setUpload({...state});
    }

    await findPhoto();

    if (album.src === undefined && state.failed.length < images.length) {
      // Premier envoi : la première photo devient la couverture
      const first = await firstPhoto();

      if (first) {
        await updateAlbum({src: first.src});
      }
    }

    setUpload(state.failed.length > 0 ? {...state} : undefined);
  }

  const firstPhoto = async (): Promise<photo | undefined> => {
    const apiFilters: ApiQueryParam = new ApiQueryParam();

    apiFilters.addFilter({field: 'albumId', value: album.albumId});
    apiFilters.addSort({by: 'createdAt', direction: 'ASC'});
    apiFilters.setCount(1);

    const result = await ApiService.get('/photo', apiFilters) as photoResultCollectionType;
    const first = result._embedded.photos[0];

    return first ? photoHydrator.hydrate(first) : undefined;
  }

  const deletePhotos = async (toDelete: photo[]): Promise<void> => {
    setBusy(true);

    try {
      for (const p of toDelete) {
        await ApiService.delete(`/photo/${p.photoId}`);
      }

      if (toDelete.some((p: photo) => p.src === album.src)) {
        await updateAlbum({src: ''});
      }
    } catch (_) {
      alert("Une erreur c'est produite.");
    } finally {
      setSelected([]);
      setBusy(false);
      await findPhoto();
    }
  }

  const setCover = async (p: photo): Promise<void> => {
    setBusy(true);

    try {
      await updateAlbum({src: p.src});
    } catch (_) {
      alert("Une erreur c'est produite.");
    } finally {
      setBusy(false);
    }
  }

  const toggle = (p: photo): void => {
    setSelected(selected.includes(p.photoId)
      ? selected.filter((id: string) => id !== p.photoId)
      : [...selected, p.photoId]
    );
  }

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  }

  return (
    <div className="p-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h6 className="m-0 me-auto">
          <i className="fa-regular fa-images me-2"/>
          Photos <span className="badge bg-primary">{photos.length}</span>
        </h6>
        {selected.length > 0 && (
          <>
            <button className="btn btn-xs btn-info" onClick={() => setSelected([])} disabled={busy}>
              Désélectionner
            </button>
            <button className="btn btn-xs btn-danger" disabled={busy}
                    onClick={() => setDel(photos.filter((p: photo) => selected.includes(p.photoId)))}>
              <i className="fa-solid fa-trash me-1"/>Supprimer la sélection ({selected.length})
            </button>
          </>
        )}
        {selected.length === 0 && photos.length > 0 && (
          <button className="btn btn-xs btn-info" onClick={() => setSelected(photos.map((p: photo) => p.photoId))} disabled={busy}>
            Tout sélectionner
          </button>
        )}
      </div>

      {loading && photos.length === 0 && (
        <TableLineLoader/>
      )}

      {!loading && photos.length === 0 && (
        <p className="text-muted text-center py-3">Aucune photo dans cet album.</p>
      )}

      <div className="row g-2">
        {photos.map((p: photo, index: number) => {
          const cover: boolean = album.src !== undefined && p.src === album.src;
          const isSelected: boolean = selected.includes(p.photoId);

          return (
            <div key={p.photoId} className="col-6 col-md-4 col-lg-3 col-xxl-2">
              <div className={`__admin_album_photo__ ${isSelected ? '__admin_album_photo_selected__' : ''}`}>
                <img src={p.src} alt="" className="cursor-pointer" onClick={() => setPreview(index)}/>

                <input type="checkbox" className="form-check-input __admin_album_photo_check__"
                       checked={isSelected} onChange={() => toggle(p)} title="Sélectionner"/>

                {cover && (
                  <span className="badge bg-warning __admin_album_photo_cover__">
                    <i className="fa-solid fa-star me-1"/>Couverture
                  </span>
                )}

                <div className="__admin_album_photo_actions__">
                  {!cover && (
                    <button className="btn btn-xs btn-light" title="Définir comme couverture" disabled={busy}
                            onClick={() => setCover(p)}>
                      <i className="fa-regular fa-star"/>
                    </button>
                  )}
                  <button className="btn btn-xs btn-danger" title="Supprimer" disabled={busy}
                          onClick={() => setDel([p])}>
                    <i className="fa-solid fa-trash"/>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={`__admin_dropzone__ mt-3 mb-3 ${dragOver ? '__admin_dropzone_over__' : ''}`}
           onClick={() => upload === undefined && inputRef.current?.click()}
           onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
           onDragLeave={() => setDragOver(false)}
           onDrop={onDrop}>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden
               onChange={(e) => {
                 uploadFiles(Array.from(e.target.files ?? []));
                 e.target.value = '';
               }}/>

        {upload === undefined || upload.done === upload.total ? (
          <>
            <i className="fa-solid fa-cloud-arrow-up fs-2 mb-2"/>
            <div className="fw-bold">Glissez vos photos ici ou cliquez pour les choisir</div>
            <div className="small text-muted">Plusieurs fichiers possibles · les images sont redimensionnées automatiquement</div>
          </>
        ) : (
          <div className="w-100 px-4">
            <div className="fw-bold mb-2">Envoi en cours : {upload.done} / {upload.total}</div>
            <div className="progress">
              <div className="progress-bar progress-bar-striped progress-bar-animated"
                   style={{width: `${Math.round(upload.done / upload.total * 100)}%`}}/>
            </div>
          </div>
        )}
      </div>

      {upload !== undefined && upload.done === upload.total && upload.failed.length > 0 && (
        <div className="alert alert-warning d-flex align-items-start">
          <div className="flex-grow-1">
            {upload.failed.length} photo{upload.failed.length > 1 ? 's' : ''} n'{upload.failed.length > 1 ? 'ont' : 'a'} pas pu être envoyée{upload.failed.length > 1 ? 's' : ''} :
            <span className="small ms-1">{upload.failed.join(', ')}</span>
          </div>
          <button className="btn-close" onClick={() => setUpload(undefined)}/>
        </div>
      )}

      {preview !== undefined && (
        <Lightbox photos={photos} index={preview} title={album.label}
                  change={setPreview} close={() => setPreview(undefined)}/>
      )}

      {del && (
        <DeleteConfirme
          message={del.length > 1 ? `Supprimer définitivement ces ${del.length} photos ?` : undefined}
          confirme={(c: boolean) => {
            if (c) {
              deletePhotos(del);
            }

            setDel(undefined);
          }}/>
      )}
    </div>
  );
}
